import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import useSWR from 'swr'
import { QueryV1, TermsQueryV1, type HitV1 } from '@ttab/elephant-api/index'
import { Block } from '@ttab/elephant-api/newsdoc'
import type { EventlogItem } from '@ttab/elephant-api/repository'
import { fetchFactboxBlocksByArticle } from '@/hooks/index/useDocuments/lib/factboxSubset'
import { useRegistry, useLink, useRepositoryEvents } from '@/hooks'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@ttab/elephant-ui'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@ttab/elephant-ui/icons'
import { factboxContentFingerprint, getSyncStateByArticle } from './getSyncStateByArticle'

const PAGE_SIZE = 25

const articleFields = [
  'document.title',
  'document.rel.section.title',
  'modified'
] as const

interface LinkedArticlesProps {
  documentId: string
}

export const LinkedArticles = ({ documentId }: LinkedArticlesProps): JSX.Element | null => {
  const { index, repository } = useRegistry()
  const { data: session } = useSession()
  const openArticle = useLink('Editor')
  const { t } = useTranslation('factbox')
  const [page, setPage] = useState(1)

  const articleQuery = useMemo(() => QueryV1.create({
    conditions: {
      oneofKind: 'terms',
      terms: TermsQueryV1.create({
        field: 'document.content.core_factbox.rel.source.uuid',
        values: [documentId]
      })
    }
  }), [documentId])

  // Articles, their factbox blocks and this factbox itself are fetched together:
  // the sync badge needs all three, and separate hooks would give the list its own
  // loading state and flash last page's icons while resolving. Saves refetch via the
  // repository event handler below, and status changes don't touch the contents.
  // The badge fetches fall back to no badges, so their failure can't hide the list.
  const { data, mutate } = useSWR(
    session?.accessToken && index && repository
      ? `linked-articles/${documentId}/${page}`
      : null,
    async () => {
      const [result, original] = await Promise.all([
        index!.query<HitV1, typeof articleFields>({
          accessToken: session!.accessToken,
          documentType: 'core/article',
          fields: articleFields,
          query: articleQuery,
          sort: [{ field: 'modified', desc: true }],
          page,
          size: PAGE_SIZE
        }),
        // Latest saved version - the badges are meant to flip on save, not on keystroke.
        repository!.getDocument({ uuid: documentId, accessToken: session!.accessToken })
          .catch(() => null)
      ])

      if (!result.ok) {
        throw new Error(result.errorMessage || 'Unknown error while fetching linked articles')
      }

      const articles = result.hits
      // From the total, so a list of exactly 25 or 50 doesn't offer an empty next page.
      const hasNext = page * PAGE_SIZE < result.total

      const articleIds = articles
        .map((article) => article.id)
        .filter((id): id is string => !!id)

      const blocksByArticle = await fetchFactboxBlocksByArticle({
        articleIds,
        repository: repository!,
        accessToken: session!.accessToken
      }).catch(() => new Map<string, Block[]>())

      return { articles, hasNext, blocksByArticle, original: original?.document }
    }
  )

  // Saving the factbox emits a repository event before any status changes, so the
  // badges would otherwise sit on the previous contents until the view remounts.
  const eventTypes = useMemo(() => ['core/factbox', 'core/factbox+meta'], [])

  const handleRepositoryEvent = useCallback(
    (event: EventlogItem) => {
      if (event.uuid === documentId || event.mainDocument === documentId) {
        void mutate()
      }
    },
    [documentId, mutate]
  )

  useRepositoryEvents(eventTypes, handleRepositoryEvent)

  const articles = data?.articles

  // The factbox document's own contents, in the same shape as an embedded copy.
  const originalFingerprint = useMemo(() => {
    if (!data?.original) {
      return undefined
    }

    return factboxContentFingerprint(Block.create({
      type: 'core/factbox',
      title: data.original.title,
      content: data.original.content
    }))
  }, [data?.original])

  const syncStateByArticle = useMemo(
    () => getSyncStateByArticle(data?.blocksByArticle, documentId, originalFingerprint),
    [data?.blocksByArticle, documentId, originalFingerprint]
  )

  // A later page can empty out, e.g. when its last article drops the embed.
  const isEmptyLaterPage = page > 1 && !!data && !data.articles.length

  useEffect(() => {
    if (isEmptyLaterPage) {
      setPage((p) => Math.max(1, p - 1))
    }
  }, [isEmptyLaterPage])

  if (page === 1 && !articles?.length) {
    return null
  }

  const hasPrev = page > 1
  const hasNext = data?.hasNext ?? false

  return (
    <div className='mx-12 mt-4 mb-4 border rounded p-3'>
      <p className='text-sm font-semibold mb-2'>{t('usedInArticles')}</p>
      <ul className='flex flex-col gap-1'>
        {articles?.map((article) => {
          const title = article.fields['document.title']?.values[0] ?? article.id
          const modified = article.fields.modified?.values[0]
          const modifiedDate = new Date(modified)?.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })

          const syncState = article.id ? syncStateByArticle.get(article.id) : 'unknown'
          const isOutOfSync = syncState === 'outOfSync'
          const isInSync = syncState === 'inSync'

          return (
            <li key={article.id}>
              <button
                className='text-sm text-left hover:underline w-full flex items-center gap-2'
                onClick={(e) => openArticle(e, { id: article.id })}
              >
                <span className='w-3.5 shrink-0 flex items-center justify-center'>
                  {isOutOfSync && (
                    <span
                      className='inline-flex items-center justify-center w-3.5 h-3.5 text-[10px] font-bold text-white dark:text-black bg-cancelled rounded-full'
                      title={t('outOfSyncTooltip')}
                    >
                      !
                    </span>
                  )}
                  {isInSync && (
                    <CheckIcon
                      size={14}
                      strokeWidth={3}
                      className='text-white bg-green-600 rounded-full p-0.5'
                      aria-label={t('inSyncTooltip')}
                    >
                      <title>{t('inSyncTooltip')}</title>
                    </CheckIcon>
                  )}
                </span>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  {`${modifiedDate} - ${title}`}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      {(hasPrev || hasNext) && (
        <div className='flex justify-center gap-1 mt-2'>
          <Button
            variant='ghost'
            size='sm'
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeftIcon size={16} />
            <span className='text-xs'>{t('previousPage')}</span>
          </Button>
          <Button
            variant='ghost'
            size='sm'
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            <span className='text-xs'>{t('nextPage')}</span>
            <ChevronRightIcon size={16} />
          </Button>
        </div>
      )}
    </div>
  )
}
